import { useMutation } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiFetch } from '../../lib/api';
import { Banner, Button, Field } from '../../components/ui';

interface ButtonSlot {
  index: number;
  label: string;
  danger: boolean;
}
interface Challenge {
  challengeId: string;
  round: number | null;
  rounds?: number;
  roundsPassed?: number;
  armed?: boolean;
  layout: ButtonSlot[] | null;
}

/**
 * Free up storage — the Owner-only wipe.
 *
 * Three rounds of nine buttons, with DELETE in a server-chosen position each
 * time and one wrong press voiding the whole thing. That is an ACCIDENT guard:
 * the browser must be told which button is red in order to draw it, so the
 * puzzle stops a hand, not a script. What stops a script is the layer below —
 * owner role, a server-held single-use challenge, and password re-entry at the
 * moment of deletion.
 */
export function Danger() {
  const queryClient = useQueryClient();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const open = useMutation({
    mutationFn: () =>
      apiFetch<Challenge>('/api/owner/maintenance/storage/challenge', {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: (data) => {
      setChallenge(data);
      setError(null);
      setDone(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const answer = useMutation({
    mutationFn: (input: { round: number; index: number }) =>
      apiFetch<Challenge>(`/api/owner/maintenance/storage/challenge/${challenge?.challengeId}`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (data) => setChallenge({ ...data, challengeId: challenge?.challengeId ?? '' }),
    onError: (err: Error) => {
      setError(err.message);
      setChallenge(null);
    },
  });

  const execute = useMutation({
    mutationFn: () =>
      apiFetch<{ vegetablesReseeded: number; accountsPreserved: number }>(
        '/api/owner/maintenance/storage/execute',
        {
          method: 'POST',
          body: JSON.stringify({
            challengeId: challenge?.challengeId,
            password,
          }),
        },
      ),
    onSuccess: async (data) => {
      setDone(
        `Shop data cleared. ${data.vegetablesReseeded} vegetables reseeded, ${data.accountsPreserved} logins untouched.`,
      );
      setChallenge(null);
      setPassword('');
      queryClient.clear();
    },
    onError: (err: Error) => setError(err.message),
  });

  const total = challenge?.rounds ?? 3;
  const passed = challenge?.roundsPassed ?? 0;

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="text-3xl text-tomato-600">Free up storage</h1>
      <p className="mt-2 text-[14.5px] leading-relaxed text-ink-700">
        This permanently deletes every store, all stock, every deal and the entire sales history,
        then puts the twelve vegetables back. It cannot be undone and there is no backup here.
      </p>

      <div className="mt-4">
        <Banner tone="info">
          <strong>Logins are kept.</strong> Wiping accounts would delete the only owner and lock
          everyone out of the application permanently, so people, roles and store assignments all
          survive. Only shop data goes.
        </Banner>
      </div>

      {done && (
        <div className="mt-5">
          <Banner tone="good">{done}</Banner>
        </div>
      )}
      {error && (
        <div className="mt-5">
          <Banner tone="error">{error}</Banner>
        </div>
      )}

      {!challenge && (
        <Button
          variant="danger"
          className="mt-6 py-2.5"
          onClick={() => open.mutate()}
          disabled={open.isPending}
        >
          Start the confirmation
        </Button>
      )}

      {challenge && !challenge.armed && challenge.layout && (
        <section className="tag mt-6 border-tomato-600/40 p-6">
          <div className="flex items-baseline justify-between">
            <h2 className="sign text-[12px] text-ink-900">
              Round {challenge.round} of {total}
            </h2>
            <span className="sign text-[10px] text-ink-500">
              {passed} passed · one mistake starts over
            </span>
          </div>

          <p className="mt-2 text-[14px] text-ink-500">
            Press the red DELETE button. It moves every round.
          </p>

          <div className="mt-5 grid grid-cols-3 gap-2.5">
            {challenge.layout.map((slot) => (
              <button
                key={slot.index}
                type="button"
                disabled={answer.isPending}
                onClick={() => answer.mutate({ round: challenge.round ?? 1, index: slot.index })}
                className={`sign rounded-[3px] border px-3 py-6 text-[11px] transition-transform active:scale-[0.97] ${
                  slot.danger
                    ? 'border-tomato-700 bg-tomato-600 text-paper-50 hover:bg-tomato-500'
                    : 'border-paper-300 bg-paper-100 text-ink-700 hover:bg-paper-200'
                }`}
              >
                {slot.label}
              </button>
            ))}
          </div>

          <Button variant="quiet" className="mt-5" onClick={() => setChallenge(null)}>
            Abandon this
          </Button>
        </section>
      )}

      {challenge?.armed && (
        <section className="tag mt-6 border-tomato-600/40 p-6">
          <h2 className="sign text-[12px] text-ink-900">Last step</h2>
          <p className="mt-2 text-[14px] text-ink-500">
            Confirm it is really you. A signed-in session on an unattended screen is not enough to
            destroy the shop.
          </p>

          <form
            className="mt-5 space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              execute.mutate();
            }}
          >
            <Field
              label="Your password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />

            <div className="flex gap-2">
              <Button type="submit" variant="danger" disabled={execute.isPending}>
                {execute.isPending ? 'Deleting…' : 'Delete everything'}
              </Button>
              <Button type="button" variant="quiet" onClick={() => setChallenge(null)}>
                Cancel
              </Button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
